'use strict';

Object.defineProperty(exports, "__esModule", {
  value: true
});

var _typeof = typeof Symbol === "function" && typeof Symbol.iterator === "symbol" ? function (obj) { return typeof obj; } : function (obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; };

exports.isObject = isObject;
exports.mergeKeyState = mergeKeyState;
exports.deepEqual = deepEqual;
exports.getKeys = getKeys;
exports.getValue = getValue;
exports.mergeChangedKeys = mergeChangedKeys;
exports.mergeMatchingKeys = mergeMatchingKeys;
exports.clearMatchingKeys = clearMatchingKeys;

var _immutable = require('immutable');

/*
 * Check if a value is an object but not null or undefined (in JS typeof null === 'object')
 */
function isObject(v) {
  return v !== null && v !== void 0 && (typeof v === 'undefined' ? 'undefined' : _typeof(v)) === "object";
}

/*
 * MERGE KEY STATE
 *
 * Private inner mutation function for merging key state recursively with mergeMatchingKeys
 */
function mergeKeyState(current, change) {
  if (change === void 0) {
    // EARLY EXIT: Change is undefined, so there is nothing to apply/do.
    return current;
  }

  if (_immutable.Map.isMap(current)) {
    return current.withMutations(mergeChangedKeys(change));
  } else if (_immutable.List.isList(current)) {
    return current.withMutations(mergeChangedKeys(change));
  } else if (!!current && current['withMutations'] !== void 0 && isObject(change)) {
    // Handle nested changes in Map-like objects (like records)
    return current.withMutations(mergeChangedKeys(change));
  } else if (isObject(current)) {
    var result = deepEqual(current, change);
    return result ? current : change;
  } else {
    return change;
  }
}

function deepEqual(oldValue, newValue) {
  if (oldValue === null || oldValue === void 0 || newValue === void 0 || newValue === null) {
    return oldValue === newValue;
  } else if (Array.isArray(oldValue) && Array.isArray(newValue)) {
    return oldValue.length === newValue.length && !oldValue.some(function (v, i) {
      return !deepEqual(v, newValue[i]);
    });
  } else if (isObject(oldValue) && isObject(newValue)) {
    var oldKeys = Object.keys(oldValue);
    var newKeys = Object.keys(newValue);
    var oldKeyMap = oldKeys.reduce(function (map, key) {
      map[key] = true;return map;
    }, {});
    return oldKeys.length === newKeys.length && newKeys.every(function (newKey) {
      return oldKeyMap[newKey];
    }) && oldKeys.every(function (oldKey) {
      return deepEqual(newValue[oldKey], oldValue[oldKey]);
    });
  } else {
    return oldValue === newValue;
  }
}

function getKeys(anImmutable) {
  var changedKeys = [];
  if (anImmutable && anImmutable.keys !== void 0) {
    changedKeys = anImmutable.keys();
  } else if (isObject(anImmutable)) {
    changedKeys = Object.keys(anImmutable);
  }
  return changedKeys;
}

function getValue(anImmutable, key) {
  var change = void 0;
  if (anImmutable && anImmutable.get !== void 0) {
    change = anImmutable.get(key);
  } else if (isObject(anImmutable) || Array.isArray(anImmutable)) {
    change = anImmutable[key];
  }
  return change;
}

function mergeChangedKeys(changes) {
  return function _mergeChangedKeys(destination) {
    var changedKeys = getKeys(changes);
    var changedKeyMap = {};

    var ret = destination.withMutations(function (dest) {
      var _iteratorNormalCompletion = true;
      var _didIteratorError = false;
      var _iteratorError = undefined;

      try {
        for (var _iterator = changedKeys[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
          var changedKey = _step.value;

          var change = getValue(changes, changedKey);
          changedKeyMap[changedKey] = change !== void 0;
          if (dest.has(changedKey)) {
            dest.set(changedKey, mergeKeyState(dest.get(changedKey), change));
          } else {
            dest.set(changedKey, change);
          }
        }
      } catch (err) {
        _didIteratorError = true;
        _iteratorError = err;
      } finally {
        try {
          if (!_iteratorNormalCompletion && _iterator.return) {
            _iterator.return();
          }
        } finally {
          if (_didIteratorError) {
            throw _iteratorError;
          }
        }
      }

      var destKeys = getKeys(dest);
      var _iteratorNormalCompletion2 = true;
      var _didIteratorError2 = false;
      var _iteratorError2 = undefined;

      try {
        for (var _iterator2 = destKeys[Symbol.iterator](), _step2; !(_iteratorNormalCompletion2 = (_step2 = _iterator2.next()).done); _iteratorNormalCompletion2 = true) {
          var destKey = _step2.value;

          if (changedKeyMap[destKey] !== true) {
            dest.remove(destKey);
          }
        }
      } catch (err) {
        _didIteratorError2 = true;
        _iteratorError2 = err;
      } finally {
        try {
          if (!_iteratorNormalCompletion2 && _iterator2.return) {
            _iterator2.return();
          }
        } finally {
          if (_didIteratorError2) {
            throw _iteratorError2;
          }
        }
      }

      return dest;
    });

    return ret;
  };
}

/*
 * MERGE MATCHING KEYS
 *
 * Similar to Immutable.js merge() but does not copy across keys that are missing in the destination Map.
 *
 * Usage:
 *
 *   let destinationMap = Immutable.Map() ; // Where destinationMap is a Map() with a fixed set of keys
 *   let sourceMap = Immutable.Map() ;  // Where sourceMap is a Map() with keys that may not be in destinationMap
 *   destinationMap = destinationMap.withMutations( mergeMatchingKeys(sourceMap) )
 *
 */
function mergeMatchingKeys(changes) {
  return function _mergeMatchingKeys(destination) {
    var keys = getKeys(changes);

    var ret = destination.withMutations(function (map) {
      var _iteratorNormalCompletion3 = true;
      var _didIteratorError3 = false;
      var _iteratorError3 = undefined;

      try {
        for (var _iterator3 = keys[Symbol.iterator](), _step3; !(_iteratorNormalCompletion3 = (_step3 = _iterator3.next()).done); _iteratorNormalCompletion3 = true) {
          var key = _step3.value;

          if (map.has(key)) {
            var change = getValue(changes, key);
            map.set(key, mergeKeyState(map.get(key), change));
          }
        }
      } catch (err) {
        _didIteratorError3 = true;
        _iteratorError3 = err;
      } finally {
        try {
          if (!_iteratorNormalCompletion3 && _iterator3.return) {
            _iterator3.return();
          }
        } finally {
          if (_didIteratorError3) {
            throw _iteratorError3;
          }
        }
      }

      return map;
    });

    return ret;
  };
}

/*
 * CLEAR KEYS
 *
 * Mutate an Immutable.js map to clear specified keys
 *
 * Usage:
 *
 *   let destinationMap = Immutable.Map() ; // Where destinationMap is a Map() with a fixed set of keys
 *   let keys = [] ;  // Where sourceMap is an array with keys that may not be in destinationMap
 *   destinationMap = destinationMap.withMutations( clearMatchingKeys(keys) )
 *
 */
function clearMatchingKeys(keys) {
  var clearValue = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : void 0;

  return function _clearKeys(destination) {
    var _iteratorNormalCompletion4 = true;
    var _didIteratorError4 = false;
    var _iteratorError4 = undefined;

    try {
      for (var _iterator4 = keys[Symbol.iterator](), _step4; !(_iteratorNormalCompletion4 = (_step4 = _iterator4.next()).done); _iteratorNormalCompletion4 = true) {
        var key = _step4.value;

        if (destination.has(key)) {
          destination.set(key, clearValue);
        }
      }
    } catch (err) {
      _didIteratorError4 = true;
      _iteratorError4 = err;
    } finally {
      try {
        if (!_iteratorNormalCompletion4 && _iterator4.return) {
          _iterator4.return();
        }
      } finally {
        if (_didIteratorError4) {
          throw _iteratorError4;
        }
      }
    }

    return destination;
  };
}